import React, { useState, useEffect, useCallback } from "react";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { filterBy } from "@progress/kendo-data-query";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router";

import withMeta from "../../utils/withMeta";
import { getPreprocessedMenuData } from "../../utils/withMeta"
import Search from "./Search";
import { buildUrl, removeAccents } from "../../utils/helper";
import Tooltip from "../../forms/Tooltip";
import "./Menu.scss";

/* let _timer = null; */

/**
 * @typedef {Object} Menu
 * @property {number} MenuID - Menu item ID
 * @property {number} NadMenuID - ID of parent item
 * @property {string} Label - Menu item label
 * @property {string} Title - Menu item title
 * @property {array} children - Children of menu item
 */

/**
 * Menu Component
 * @module
 * @param {Menu} menu - Menu
 * @param {string} sidebarType - Sidebar type ('normal','compact','fullscreen')
 * @param {func} onSelect - Function invoked on item select
 * @param {func} [onMenuFilter] -Function invoked on menu filter
 */

const Menu = (props) => {
  const [search, setSearch] = useState("")
  const [menu, setMenu] = useState({ children: [] });
  const [selected, setSelected] = useState("")
  const [expanded, setExpanded] = useState([])
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * @function buildMenu - vyrobi menu pre @function render
   * @param rootMenuItem - koren menu
   * @param depth - hlbka @var rootMenuItem v strome poloziek menu
   * @returns @var PanelBarItem - jedna polozka v menu alebo strom
   */

  const buildMenu = () => {
    if (location.pathname !== '/prihlasenie' && props.menu.Route) {
      return (
        <Tooltip position="right">
          <PanelBar
            className="items"
            expandMode={"multiple"}
            onSelect={handleSelect}
            isControlled={true}
            selected={selected}
            expanded={expanded}
          >
            {menu.children.map((menuItem) => buildMenuItems(menuItem, 0))}
          </PanelBar>
        </Tooltip>
      );
    }
  };

  const isItemExpanded = useCallback((rootMenuItems, location) => {
    if (!Array.isArray(rootMenuItems)) {
      return false
    }
    return rootMenuItems.some(val => {
      const newRoute = val.Route?.split("#")[0] //odstránenie parametrov pre dashboard

      if (newRoute === location.pathname || (location.pathname === "/menu" && location.state?.id === val.MenuID)) return true;
      if (Array.isArray(val.children) && isItemExpanded(val.children, location)) return true;
      return false;
    })
  }, [])

  const getSelected = useCallback((menu) => {
    let menuKeys = [];
    let cnt = 0;
    menu.map(el => {
      cnt++;
      if (menuItemHasChildren(el) && isItemExpanded(el.children, location)) {
        menuKeys = [...menuKeys, cnt - 1, ...getSelected(el.children)]
      }
      const newRoute = el.Route?.split("#")[0] //odstránenie parametrov pre dashboard
      if (newRoute === location.pathname || (location.pathname === "/menu" && location.state?.id === el.MenuID)) {
        menuKeys = [...menuKeys, cnt - 1]
      }
      return el;
    })
    return menuKeys;
  }, [isItemExpanded, location])

  const getDefaultSelected = useCallback((newMenu) => {
    const newSelected = getSelected(newMenu.children)
    const defaultSelected = "." + newSelected.join('.');

    const defaultExpanded = newSelected.map((x, i) => {
      return "." + newSelected.slice(0, i + 1).join('.');
    });
    setExpanded(defaultExpanded)
    setSelected(defaultSelected)
  }, [getSelected])


  useEffect(() => {
    if (props.menu.children.length !== 0) {
      setMenu(props.menu)
      getDefaultSelected(props.menu);
      setSearch("")
    }
  }, [props.menu, getDefaultSelected]);

  const buildNotificationCount = (rootMenuItem) => {
    return (
      <span className="menuItemCount">
        {rootMenuItem.CNT}
      </span>
    );
  };

  const buildItemWithNofication = (rootMenuItem, depth) => {
    if (menuItemHasChildren(rootMenuItem, depth)) {
      const isItemExpandedValue = isItemExpanded(rootMenuItem.children, location)
      return (
        <PanelBarItem
          disabled={rootMenuItem.Visible === 2}
          imageUrl={buildUrl(rootMenuItem.Icon)}
          key={rootMenuItem.MenuID}
          menuId={rootMenuItem.MenuID}
          title={
            <div className="custom-tooltip" title={rootMenuItem.Tooltip}>
              {rootMenuItem.Title}
              {buildNotificationCount(rootMenuItem, depth)}
            </div>
          }
          route={rootMenuItem.Route}
          className={`parent indent-${depth}`}
          expanded={isItemExpandedValue ? true : false}
        >
          {rootMenuItem.children.map((menuItem) =>
            buildMenuItems(menuItem, depth + 1)
          )}
        </PanelBarItem>
      );
    }
    return (
      <PanelBarItem
        disabled={rootMenuItem.Visible === 2}
        imageUrl={buildUrl(rootMenuItem.Icon)}
        key={rootMenuItem.MenuID}
        menuId={rootMenuItem.MenuID}
        title={
          <div className="custom-tooltip" title={rootMenuItem.Tooltip}>
            {rootMenuItem.Title}
            {buildNotificationCount(rootMenuItem, depth)}
          </div>
        }
        route={rootMenuItem.Route}
        className={`indent-${depth}`}
      />
    );
  };

  const buildItem = (rootMenuItem, depth) => {
    if (menuItemHasChildren(rootMenuItem, depth)) {
      const isItemExpandedValue = isItemExpanded(rootMenuItem.children, location)
      return (
        <PanelBarItem
          disabled={rootMenuItem.Visible === 2}
          imageUrl={buildUrl(rootMenuItem.Icon)}
          key={rootMenuItem.MenuID}
          menuId={rootMenuItem.MenuID}
          title={
            <div className="custom-tooltip" title={rootMenuItem.Tooltip}>
              {rootMenuItem.Title}
            </div>
          }
          route={rootMenuItem.Route}
          className={`parent indent-${depth}`}
          expanded={isItemExpandedValue ? true : false}
        >
          {rootMenuItem.children.map((menuItem) =>
            buildMenuItems(menuItem, depth + 1)
          )}
        </PanelBarItem>
      );
    } else {
      return (
        <PanelBarItem
          disabled={rootMenuItem.Visible === 2}
          imageUrl={buildUrl(rootMenuItem.Icon)}
          key={rootMenuItem.MenuID}
          menuId={rootMenuItem.MenuID}
          title={
            <div className="custom-tooltip" title={rootMenuItem.Tooltip}>
              {rootMenuItem.Title}
            </div>
          }
          route={rootMenuItem.Route}
          className={`indent-${depth}`}
        />
      );
    }
  };

  const buildMenuItems = (rootMenuItem, depth) => {
    if (rootMenuItem.CNT) {
      return buildItemWithNofication(rootMenuItem, depth);
    } else {
      return buildItem(rootMenuItem, depth);
    }
  };

  /**
   * @function menuItemHasChildren - zisti, ci @param menuItem ma potomkov
   * @param menuItem - polozka menu
   * @returns @var bool - true alebo false
   */
  const menuItemHasChildren = (menuItem) => {
    return menuItem.children && menuItem.children.length > 0;
  };

  const handleSelect = (event) => {
    const m = event.target.props;
    let newSelected = selected;

    let newExpanded = [];
    let isExpanded;
    if (expanded.includes(m.uniquePrivateKey)) {
      newExpanded = expanded.filter((x) => x !== m.uniquePrivateKey);
      isExpanded = true;
    } else {
      newExpanded = [...expanded, m.uniquePrivateKey];
      isExpanded = false;
    }

    const routeHistory = props.history;
    const targetRoute = m.route;

    if (!m.children) {
      if (!location || targetRoute !== location.pathname) {
        if (targetRoute) {
          newSelected = m.uniquePrivateKey;
          navigate(targetRoute);
          props.onSelect(targetRoute, location, routeHistory, !isExpanded);
        }
        else {
          newSelected = m.uniquePrivateKey;
          navigate("/menu", { state: { id: m.menuId } })
        }
      }
    } else {
      if (m.route && m.route !== "/" && !m.route.endsWith(":id")) {
        props.onSelect(
          targetRoute,
          location,
          routeHistory,
          !isExpanded
        );

        if (!isExpanded) {
          newSelected = m.uniquePrivateKey;
          navigate(targetRoute);

          for (let i = 0; i < m.children.length; i++) {
            const x = m.children[i];
            if (x.props.route === targetRoute) {
              newSelected = x.props.uniquePrivateKey;
              break;
            }
          }
        }
      }
      else {
        if (!(location.pathname === "/menu" && location.state.id === m.menuId)) {
          navigate("/menu", { state: { id: m.menuId } })
        }
      }
    }

    setSelected(newSelected);
    setExpanded(newExpanded)
  };

  const getParents = (item) => {
    if (item.NadMenuID) {
      const parentItem = props.meta.menu.find(x => x.MenuID === item.NadMenuID)
      const parents = getParents(parentItem)
      return [props.meta.menu.find(x => x.MenuID === item.MenuID), ...parents]
    }
    return [item]
  }

  const getFullMenuList = (list) => {
    let newList = []
    list.forEach(item => {
      newList = [...newList, ...getParents(item)]
    });

    //remove copy
    const ids = newList.map(o => o.MenuID)
    const filtered = newList.filter(({ MenuID }, index) => !ids.includes(MenuID, index + 1))
    return filtered;
  }

  const getAllExtended = (children, parent) => {
    let newExtended = []
    children.forEach((x, i) => {
      const item = `${parent}.${i}`
      newExtended.push(item);
      if (x.children && x.children.length) {
        newExtended = [...newExtended, ...getAllExtended(x.children, item)];
      }
    })

    return newExtended
  }

  const handleChange = (name, value) => {
    if (name === "search") {
      const cleanMenu = props.meta.menu.map(x => ({ ...x, Title: removeAccents(x.Title) }))
      let newMenu = filterBy(cleanMenu, {
        logic: "and",
        filters: [
          {
            field: "Title",
            operator: "contains",
            value: removeAccents(value),
          },
        ],
      });
      const fullList = getFullMenuList(newMenu);
      const tree = getPreprocessedMenuData(fullList.find(x => x.NadMenuID === null), fullList.filter(x => x.Visible !== 3));

      setSearch(value)
      setMenu(tree)
      if (!value) {
        setExpanded([])
      }
      else {
        setExpanded(getAllExtended(tree.children, ""))
      }
    }
  };

  const handleClick = () => {
    setSearch("")
    setMenu(props.menu)
    setExpanded([])
  };

  return (
    <div id="menu" className={props.sidebarType}>
      <Search
        className={props.sidebarType}
        search={search}
        onChange={handleChange}
        sidebarType={props.sidebarType}
        onClick={handleClick}
      />
      {buildMenu()}
    </div>
  );
}

Menu.propTypes = {
  menu: PropTypes.shape({
    MenuID: PropTypes.number,
    NadMenuID: PropTypes.number,
    Label: PropTypes.string,
    Title: PropTypes.string,
    children: PropTypes.array,
  }),
  sidebarType: PropTypes.oneOf(["normal", "compact", "fullscreen"]),
  onSelect: PropTypes.func.isRequired,
  onMenuFilter: PropTypes.func,
};

export default withMeta(Menu);
